'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChangeEvent, ReactNode, UIEvent } from 'react';
import { useRef } from 'react';
import { ACCEPTED_ATTACHMENT_TYPES } from '@/features/messaging';
import type { ChatAnexoUi, ChatAutomacaoUi, ChatComposerUi, ChatEncerramentoUi, ChatJanelaUi, ChatMessageUi, ChatScrollUi, IaStatusUi } from '../../../types/negociacaoDetailUiTypes';
import { Button, EmptyState, IconButton, TextArea } from '@/shared/components/ui';
import { FeatherAlertCircle, FeatherFileText, FeatherImage, FeatherMessageSquare, FeatherPaperclip, FeatherPause, FeatherPauseCircle, FeatherPlay, FeatherSend, FeatherX, FeatherArchive, FeatherClock } from '@subframe/core';
import { ChatBlockedBanner } from './ChatBlockedBanner';
import { JanelaAtendimentoBanner } from '@/shared/components/features/JanelaAtendimentoBanner';
import { NegociacaoChatMessage } from './NegociacaoChatMessage';
import { OfertaNotificacaoChatCard } from './OfertaNotificacaoChatCard';
const ATTACHMENT_ACCEPT = `${ACCEPTED_ATTACHMENT_TYPES.map(type => type.mime).join(',')},.pdf,.jpg,.jpeg,.png,.docx`;
interface NegociacaoChatTabProps {
  chatMessages: ChatMessageUi[];
  automacao: ChatAutomacaoUi;
  composer: ChatComposerUi;
  encerramento: ChatEncerramentoUi;
  scroll: ChatScrollUi;
  janela?: ChatJanelaUi;
  anexo?: ChatAnexoUi;
  direcionamento?: ReactNode;
  showOfertaNotificacao?: boolean;
  onOfertaNotificacaoClick?: () => void;
}
const getIaStatusIcon = (iconKey: IaStatusUi['iconKey']) => {
  if (iconKey === 'FAILED') return <FeatherAlertCircle className="h-4 w-4" />;
  if (iconKey === 'CANCELLED') return <FeatherArchive className="h-4 w-4" />;
  if (iconKey === 'PENDING') return <FeatherClock className="h-4 w-4" />;
  if (iconKey === 'PAUSED') return <FeatherPause className="h-4 w-4" />;
  return <FeatherPlay className="h-4 w-4" />;
};
export function NegociacaoChatTab(t0) {
  const $ = _c(71);
  const {
    chatMessages,
    automacao,
    composer,
    encerramento,
    scroll,
    janela,
    anexo,
    direcionamento,
    showOfertaNotificacao: t1,
    onOfertaNotificacaoClick
  } = t0;
  const showOfertaNotificacao = t1 === undefined ? false : t1;
  const fileInputRef = useRef(null);
  const {
    containerRef,
    bottomRef,
    scrollTopRef,
    shouldAutoScrollRef
  } = scroll;
  let t2;
  if ($[0] !== automacao.iaStatusUi.iconKey) {
    t2 = getIaStatusIcon(automacao.iaStatusUi.iconKey);
    $[0] = automacao.iaStatusUi.iconKey;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const iaStatusIcon = t2;
  const hasAttachment = anexo?.pending != null;
  let t3;
  if ($[2] !== composer.canSend || $[3] !== composer.value || $[4] !== hasAttachment) {
    t3 = !composer.value.trim() && !hasAttachment || !composer.canSend;
    $[2] = composer.canSend;
    $[3] = composer.value;
    $[4] = hasAttachment;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const isSendDisabled = t3;
  const janelaExpirada = janela?.status === "EXPIRADA";
  const showResumeBanner = janelaExpirada && (janela?.canResume ?? !automacao.isReadOnly) && !encerramento.isClosed;
  const showSituacaoBanner = encerramento.isClosed && encerramento.permiteReabrir !== undefined;
  const isEncerradaSemResposta = encerramento.isClosed && encerramento.isSemResposta;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      fileInputRef.current?.click();
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleClipClick = t4;
  let t5;
  if ($[7] !== anexo) {
    t5 = e => {
      const file = e.target.files?.[0];
      if (file) {
        anexo?.onSelect(file);
      }
      e.target.value = "";
    };
    $[7] = anexo;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleFileInputChange = t5;
  let t6;
  if ($[9] !== isEncerradaSemResposta || $[10] !== showSituacaoBanner) {
    t6 = !showSituacaoBanner && isEncerradaSemResposta && <div className="flex shrink-0 items-center gap-3 border-b border-solid border-neutral-200 px-4 py-2"><div className="bg-warning-50 text-warning-600 flex h-8 w-8 items-center justify-center rounded-full"><FeatherPauseCircle className="h-4 w-4" /></div><div className="text-warning-600 flex flex-col gap-1"><span className="text-caption-bold font-caption-bold text-default-font">Negociação sem resposta</span><span className="text-subtext-color text-[11px]">Encerramento por ausência de resposta da contraparte</span></div></div>;
    $[9] = isEncerradaSemResposta;
    $[10] = showSituacaoBanner;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== automacao.canToggle || $[13] !== automacao.iaStatusUi.bannerClass || $[14] !== automacao.iaStatusUi.iconClass || $[15] !== automacao.iaStatusUi.iconKey || $[16] !== automacao.iaStatusUi.message || $[17] !== automacao.iaStatusUi.title || $[18] !== automacao.isIAPaused || $[19] !== automacao.isReadOnly || $[20] !== automacao.isToggleLoading || $[21] !== automacao.onToggle || $[22] !== iaStatusIcon || $[23] !== isEncerradaSemResposta || $[24] !== showSituacaoBanner) {
    t7 = !showSituacaoBanner && !isEncerradaSemResposta && <div className={`flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-solid px-4 py-2 transition-colors ${automacao.iaStatusUi.bannerClass}`}><div className="flex items-center gap-3"><div className={`flex h-8 w-8 items-center justify-center rounded-full ${automacao.iaStatusUi.iconClass}`}>{iaStatusIcon}</div><div className="flex flex-col"><span className="text-caption-bold font-caption-bold text-default-font">{automacao.iaStatusUi.iconKey === "PAUSED" ? "Sua equipe \xB7 Atendimento humano" : automacao.iaStatusUi.iconKey === "ACTIVE" ? "Lexi \xB7 Atendimento autom\xE1tico" : automacao.iaStatusUi.title}</span><span className="text-subtext-color text-xs">{automacao.iaStatusUi.iconKey === "PAUSED" ? "A Lexi est\xE1 pausada. Sua equipe conduz o atendimento por aqui." : automacao.iaStatusUi.message}</span></div></div>{!automacao.isReadOnly ? <Button variant={automacao.isIAPaused ? "brand-primary" : "neutral-secondary"} size="small" onClick={automacao.onToggle} disabled={!automacao.canToggle || automacao.isToggleLoading} loading={automacao.isToggleLoading}>{automacao.isIAPaused ? "Retomar com a Lexi" : "Assumir atendimento"}</Button> : null}</div>;
    $[12] = automacao.canToggle;
    $[13] = automacao.iaStatusUi.bannerClass;
    $[14] = automacao.iaStatusUi.iconClass;
    $[15] = automacao.iaStatusUi.iconKey;
    $[16] = automacao.iaStatusUi.message;
    $[17] = automacao.iaStatusUi.title;
    $[18] = automacao.isIAPaused;
    $[19] = automacao.isReadOnly;
    $[20] = automacao.isToggleLoading;
    $[21] = automacao.onToggle;
    $[22] = iaStatusIcon;
    $[23] = isEncerradaSemResposta;
    $[24] = showSituacaoBanner;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== scrollTopRef || $[27] !== shouldAutoScrollRef) {
    t8 = e_0 => {
      const el = e_0.currentTarget;
      scrollTopRef.current = el.scrollTop;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      shouldAutoScrollRef.current = distanceFromBottom <= 80;
    };
    $[26] = scrollTopRef;
    $[27] = shouldAutoScrollRef;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== encerramento.data || $[30] !== encerramento.motivo || $[31] !== encerramento.onReabrir || $[32] !== encerramento.permiteReabrir || $[33] !== encerramento.tipo || $[34] !== janelaExpirada || $[35] !== showSituacaoBanner) {
    t9 = showSituacaoBanner && <div className="shrink-0"><ChatBlockedBanner tipoEncerramento={encerramento.tipo ?? null} dataEncerramento={encerramento.data ?? null} motivoEncerramento={encerramento.motivo || undefined} permiteReabrir={encerramento.permiteReabrir ?? false} janelaExpirada={janelaExpirada} onReabrir={encerramento.onReabrir} /></div>;
    $[29] = encerramento.data;
    $[30] = encerramento.motivo;
    $[31] = encerramento.onReabrir;
    $[32] = encerramento.permiteReabrir;
    $[33] = encerramento.tipo;
    $[34] = janelaExpirada;
    $[35] = showSituacaoBanner;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== chatMessages || $[38] !== isEncerradaSemResposta) {
    t10 = chatMessages.length === 0 ? <EmptyState icon={<FeatherMessageSquare className="text-neutral-500" />} title="Sem mensagens ainda" description={"Assim que a negocia\xE7\xE3o iniciar, as mensagens aparecer\xE3o aqui."} className="py-8" /> : chatMessages.map(msg => <NegociacaoChatMessage key={msg.id} sender={msg.sender} text={msg.text} metadata={msg.metadata} time={msg.time} isIA={msg.isIA} isOutbound={msg.isOutbound} muted={isEncerradaSemResposta} type={msg.type} mediaUrl={msg.mediaUrl} />);
    $[37] = chatMessages;
    $[38] = isEncerradaSemResposta;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== bottomRef) {
    t11 = <div ref={bottomRef} />;
    $[40] = bottomRef;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== containerRef || $[43] !== t10 || $[44] !== t11 || $[45] !== t8 || $[46] !== t9) {
    t12 = <div ref={containerRef} data-testid="negociacao-chat-thread" className="flex min-h-32 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain p-4" onScroll={t8}>{t9}{t10}{t11}</div>;
    $[42] = containerRef;
    $[43] = t10;
    $[44] = t11;
    $[45] = t8;
    $[46] = t9;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== direcionamento) {
    t13 = direcionamento ? <div className="px-6">{direcionamento}</div> : null;
    $[48] = direcionamento;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] !== janela || $[51] !== showResumeBanner) {
    t14 = showResumeBanner && janela && <JanelaAtendimentoBanner nuncaRespondeu={janela.nuncaRespondeu} onResume={janela.onResume} isResending={janela.isResuming} resumeError={janela.resumeError} />;
    $[50] = janela;
    $[51] = showResumeBanner;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== anexo || $[54] !== automacao.isIAPaused || $[55] !== composer || $[56] !== handleFileInputChange || $[57] !== hasAttachment || $[58] !== isSendDisabled || $[59] !== janelaExpirada || $[60] !== onOfertaNotificacaoClick || $[61] !== showOfertaNotificacao || $[62] !== showSituacaoBanner) {
    t15 = showOfertaNotificacao ? <div className="border-t border-solid border-neutral-100 bg-neutral-50/30 p-4"><OfertaNotificacaoChatCard onEnviar={() => onOfertaNotificacaoClick?.()} /></div> : showSituacaoBanner ? null : janelaExpirada ? composer.disabledReason ? <div className="text-subtext-color flex shrink-0 items-center gap-2 border-t border-neutral-100 px-4 py-3 text-xs"><FeatherAlertCircle className="h-3.5 w-3.5 shrink-0" /><span>{composer.disabledReason}</span></div> : null : <div className="shrink-0 border-t border-solid border-neutral-100 bg-neutral-50/30 p-3"><div className="flex flex-col gap-3"><div className="mb-1 flex items-center justify-between gap-2">{composer.disabledReason ? <div className="text-subtext-color flex items-center gap-2 text-[11px]"><FeatherAlertCircle className="h-3.5 w-3.5" /><span>{composer.disabledReason}</span></div> : <span />}</div>{!automacao.isIAPaused && composer.canSend && <div className="text-subtext-color mb-1 flex items-center gap-2 text-[11px]"><FeatherAlertCircle className="h-3.5 w-3.5" /><span>Ao enviar uma mensagem, a IA será pausada automaticamente.</span></div>}{anexo?.error ? <div className="text-error-700 mb-1 flex items-center gap-2 text-[11px]"><FeatherAlertCircle className="h-3.5 w-3.5" /><span>{anexo.error}</span></div> : null}{anexo?.pending ? <div className="flex items-center gap-3 rounded-[16px] border border-solid border-neutral-200 bg-white p-3"><div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[8px] border border-solid border-neutral-100 bg-neutral-50 text-neutral-500">{anexo.pending.tipo === "IMAGE" ? <FeatherImage /> : <FeatherFileText />}</div><div className="flex min-w-0 flex-1 flex-col"><span className="text-body-bold font-body-bold text-default-font truncate">{anexo.pending.name}</span><span className="text-caption text-subtext-color">{anexo.pending.sizeLabel}</span></div><IconButton variant="neutral-tertiary" size="small" icon={<FeatherX />} onClick={anexo.onRemove} /></div> : null}<div className="flex items-end gap-3">{anexo ? <><input ref={fileInputRef} type="file" accept={ATTACHMENT_ACCEPT} className="hidden" onChange={handleFileInputChange} /><IconButton variant="neutral-tertiary" icon={<FeatherPaperclip />} disabled={!composer.canSend || hasAttachment} onClick={handleClipClick} /></> : null}<div className="flex-1"><TextArea className="min-h-[56px]"><TextArea.Input placeholder="Escreva sua mensagem aqui..." value={composer.value} onChange={e_1 => composer.onChange(e_1.target.value)} className="min-h-[56px]" disabled={!composer.canSend} onKeyDown={e_2 => {
                if (e_2.key === "Enter" && !e_2.shiftKey) {
                  e_2.preventDefault();
                  if (!hasAttachment) {
                    composer.onSend();
                  }
                }
              }} /></TextArea></div><IconButton variant="brand-primary" icon={<FeatherSend />} disabled={isSendDisabled} onClick={composer.onSend} /></div></div></div>;
    $[53] = anexo;
    $[54] = automacao.isIAPaused;
    $[55] = composer;
    $[56] = handleFileInputChange;
    $[57] = hasAttachment;
    $[58] = isSendDisabled;
    $[59] = janelaExpirada;
    $[60] = onOfertaNotificacaoClick;
    $[61] = showOfertaNotificacao;
    $[62] = showSituacaoBanner;
    $[63] = t15;
  } else {
    t15 = $[63];
  }
  let t16;
  if ($[64] !== t12 || $[65] !== t13 || $[66] !== t14 || $[67] !== t15 || $[68] !== t6 || $[69] !== t7) {
    t16 = <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto rounded-[16px] border border-solid border-neutral-200 bg-white shadow-sm">{t6}{t7}{t12}{t13}{t14}{t15}</div>;
    $[64] = t12;
    $[65] = t13;
    $[66] = t14;
    $[67] = t15;
    $[68] = t6;
    $[69] = t7;
    $[70] = t16;
  } else {
    t16 = $[70];
  }
  return t16;
}